import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { morgansClient } from '../../../lib/connect/clients';
import { registryRecordClient } from '../records/registryRecordClient';
import { useRegistryRecordsEnabled } from '../registryFlags';
function useSummaries(kind, ids, load) {
    const enabled = useRegistryRecordsEnabled();
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            'registry',
            'list-summaries',
            user?.tenant_id,
            user?.id,
            kind,
            ids
        ],
        enabled: enabled && ids.length > 0,
        // Related records can change while this tab is unmounted (for example,
        // adding a company's contact). Refresh enrichments when returning.
        staleTime: 0,
        retry: false,
        queryFn: async ()=>{
            try {
                const rows = [];
                for(let offset = 0; offset < ids.length; offset += 100){
                    const page = await load(ids.slice(offset, offset + 100));
                    rows.push(...page.rows);
                }
                return rows;
            } catch (error) {
                console.warn(`Registry ${kind} enrichment unavailable`, error);
                throw error;
            }
        }
    });
}
export const useCompanySummaries = (ids)=>useSummaries('companies', ids, (ids)=>registryRecordClient.listCompanyRegistrySummaries({
            ids
        }));
export const useContactSummaries = (ids)=>useSummaries('contacts', ids, (ids)=>registryRecordClient.listContactRegistrySummaries({
            ids
        }));
export const useGovOrgSummaries = (ids)=>useSummaries('govorgs', ids, (ids)=>registryRecordClient.listGovernmentOrgRegistrySummaries({
            ids
        }));
/** Referenced mode is a server-scoped row source, separate from enrichment. */ export function useReferencedGovOrgs(active) {
    const enabled = useRegistryRecordsEnabled();
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            'registry',
            'list-summaries',
            user?.tenant_id,
            user?.id,
            'govorgs-referenced'
        ],
        enabled: enabled && active,
        retry: false,
        queryFn: async ()=>{
            try {
                const response = await registryRecordClient.listGovernmentOrgRegistrySummaries({
                    referencedOnly: true
                });
                return response.rows;
            } catch (error) {
                console.warn('Registry referenced government organizations unavailable', error);
                throw error;
            }
        }
    });
}
export const useVehicleSummaries = (ids)=>useSummaries('vehicles', ids, (ids)=>registryRecordClient.listVehicleRegistrySummaries({
            ids
        }));
export const useContractSummaries = (ids)=>useSummaries('contracts', ids, (ids)=>registryRecordClient.listRegistryContractSummaries({
            ids
        }));
export const useCapabilitySummaries = (ids)=>useSummaries('capabilities', ids, (ids)=>registryRecordClient.listCapabilityRegistrySummaries({
            ids
        }));
const NOTICE_COUNT_CHUNK = 200;
/** Notice totals for one page of orgs, batched; a viewer without opportunity read sees none. */ export function useGovOrgNoticeCounts(ids) {
    const enabled = useRegistryRecordsEnabled();
    const { user } = useAuth();
    return useQuery({
        queryKey: [
            'registry',
            'list-summaries',
            user?.tenant_id,
            user?.id,
            'govorg-notices',
            ids
        ],
        enabled: enabled && ids.length > 0,
        staleTime: 0,
        retry: false,
        queryFn: async ()=>{
            const counts = new Map();
            for(let offset = 0; offset < ids.length; offset += NOTICE_COUNT_CHUNK){
                const res = await morgansClient.countNoticesForGovOrgs({
                    governmentOrganizationIds: ids.slice(offset, offset + NOTICE_COUNT_CHUNK)
                });
                for (const c of res.counts)counts.set(c.governmentOrganizationId, {
                    total: c.noticesTotal,
                    open: c.noticesOpen
                });
            }
            return counts;
        }
    });
}
